<template>
  <div>
    <b-loading
      :is-full-page="true"
      :active="isLoading"
      :can-cancel="false"
    ></b-loading>

    <h2 class="subtitle">{{ $t("settings.accessControl") }}</h2>

    <div class="columns">
      <div class="column" v-if="accessDevices.length">
        <b-field :label="$t('settings.allowOpeningWithoutPaymentMethods')">
          <b-switch
            size="is-small"
            class="switch"
            type="is-primary"
            v-model="accessWithoutPaymentMethodsStatus"
            passive-type="is-secondary"
            @input="setAccessWithoutPaymentMethods"
          ></b-switch>

          {{
            accessWithoutPaymentMethodsStatus
              ? $t("common.yes")
              : $t("common.no")
          }}
        </b-field>
      </div>

      <div class="column">
        <b-field :label="$t('settings.showOpenDoorButtonInCart')">
          <b-switch
            size="is-small"
            class="switch"
            type="is-primary"
            v-model="storeBeingManaged.configs.showOpenDoorButtonInCart"
            passive-type="is-secondary"
            @input="setShowOpenDoorButtonInCart"
          ></b-switch>

          {{
            storeBeingManaged.configs.showOpenDoorButtonInCart
              ? $t("common.yes")
              : $t("common.no")
          }}
        </b-field>
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <b-field :label="$t('settings.restrictAccessToAllowList')">
          <b-switch
            size="is-small"
            class="switch"
            type="is-primary"
            v-model="storeBeingManaged.configs.restrictAccessToAllowList"
            passive-type="is-secondary"
            @input="setAllowListAccessRestriction"
          ></b-switch>

          {{
            storeBeingManaged.configs.restrictAccessToAllowList
              ? $t("common.yes")
              : $t("common.no")
          }}
        </b-field>
      </div>

      <div
        class="column"
        v-if="storeBeingManaged.configs.restrictAccessToAllowList"
      >
        <button class="button" @click="isAllowListModalOpen = true">
          <b-icon icon="list"></b-icon>
          <span>{{ $t("settings.manageAllowList") }}</span>
        </button>
      </div>
    </div>

    <b-modal :active.sync="isAllowListModalOpen">
      <allow-list-modal />
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../mixins/common";
import allowListModal from "./AllowListModal";

export default {
  mixins: [common],

  components: {
    allowListModal
  },

  data() {
    return {
      isLoading: false,
      isAllowListModalOpen: false,
      accessWithoutPaymentMethodsStatus: false
    };
  },

  computed: {
    ...mapGetters(["storeBeingManaged"]),

    accessDevices() {
      return this.storeBeingManaged.accessDevices || [];
    }
  },

  watch: {
    accessDevices: {
      immediate: true,
      handler(accessDevices) {
        this.accessWithoutPaymentMethodsStatus =
          accessDevices.length > 0 &&
          accessDevices.every(accessDevice => {
            return accessDevice.openWithoutRegisteredPaymentMethods;
          });
      }
    }
  },

  methods: {
    setAccessWithoutPaymentMethods(status) {
      const accessDeviceIds = this.accessDevices.map(accessDevice => {
        return accessDevice._id;
      });
      if (!accessDeviceIds.length) return;

      const previousStatus =
        this.accessDevices.length > 0 &&
        this.accessDevices.every(accessDevice => {
          return accessDevice.openWithoutRegisteredPaymentMethods;
        });

      this.isLoading = true;
      this.$store
        .dispatch("setAccessWithoutPaymentMethods", {
          status,
          accessDeviceIds
        })
        .then(res => {
          if (res.data.result === "ok") {
            this.$buefy.toast.open({
              message: `${this.$t("settings.doorSetTo")} ${
                status
                  ? this.$t("settings.open")
                  : this.$t("settings.notOpen")
              }!`,
              type: "is-success",
              position: "is-bottom",
              duration: 5000
            });
          } else {
            this.accessWithoutPaymentMethodsStatus = previousStatus;
            this.alertError(res.data);
          }
        })
        .catch(() => {
          this.accessWithoutPaymentMethodsStatus = previousStatus;
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    setShowOpenDoorButtonInCart() {
      this.isLoading = true;
      this.$store
        .dispatch("setShowOpenDoorButtonInCart", {
          status: this.storeBeingManaged.configs.showOpenDoorButtonInCart,
          storeId: this.storeBeingManaged._id
        })
        .then(res => {
          if (res.data.result === "ok") {
            this.$buefy.toast.open({
              message: this.$t("settings.configUpdated"),
              type: "is-success",
              position: "is-bottom",
              duration: 5000
            });
          } else {
            this.storeBeingManaged.configs.showOpenDoorButtonInCart = !this
              .storeBeingManaged.configs.showOpenDoorButtonInCart;
            this.alertError(res.data);
          }
        })
        .catch(() => {
          this.storeBeingManaged.configs.showOpenDoorButtonInCart = !this
            .storeBeingManaged.configs.showOpenDoorButtonInCart;
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    setAllowListAccessRestriction() {
      this.isLoading = true;
      this.$store
        .dispatch("setAllowListAccessRestriction", {
          status: this.storeBeingManaged.configs.restrictAccessToAllowList,
          storeId: this.storeBeingManaged._id
        })
        .then(res => {
          if (res.data.result === "ok") {
            this.$buefy.toast.open({
              message: this.$t("settings.configUpdated"),
              type: "is-success",
              position: "is-bottom",
              duration: 5000
            });
          } else {
            this.storeBeingManaged.configs.restrictAccessToAllowList = !this
              .storeBeingManaged.configs.restrictAccessToAllowList;
            this.alertError(res.data);
          }
        })
        .catch(() => {
          this.storeBeingManaged.configs.restrictAccessToAllowList = !this
            .storeBeingManaged.configs.restrictAccessToAllowList;
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }
};
</script>

<style scoped>
.switch {
  position: relative;
  top: 2px;
}
</style>
